import clsx from 'clsx'
import { campaignApi } from 'features/campaigns'
import {
	type FormEventHandler,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState
} from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import Select, { type MultiValue, type SingleValue } from 'react-select'

import type { ApiHubSpot } from '../../../api/hubspot'

import { hubSpotApi } from '../../../api/hubspot/api.ts'
import { FullLoader } from '../../atoms/FullLoader/FullLoader'
import { ResponsiveButton } from '../../atoms/responsiveButton'
import css from './CampaignsHubspot.module.scss'
import { adaptToServer, adaptToState, isSame, type Values } from './utils'

const INITIAL: Values = {
	lead: [],
	sub: undefined
}

export function CampaignsHubspot() {
	const sourceId = useId()
	const subSourceId = useId()

	const { id = '' } = useParams<{ id?: string }>()
	const { push } = useHistory()

	const campaignQuery = campaignApi.useGetByIdQuery(id, {
		skip: !id
	})

	const optionsQuery = hubSpotApi.useOptionsQuery('lead_source')
	const subOptionsQuery = hubSpotApi.useOptionsQuery('contact_sub_source')
	const settingsQuery = hubSpotApi.useSettingsQuery(id)
	const [update, updateOptions] = hubSpotApi.useUpdateSettingsMutation()

	const sourceOptions = optionsQuery.data ?? INITIAL.lead
	const subSourceOptions = subOptionsQuery.data ?? INITIAL.lead
	const [lead, setLead] = useState(INITIAL.lead)
	const campaignName = campaignQuery.data?.name ?? ''
	const [sub, setSub] = useState(INITIAL.sub)

	const initial = useRef(INITIAL)

	const isInitialValues = useMemo(
		() => isSame({ lead, sub }, initial.current),
		[sub, lead]
	)

	useEffect(() => {
		if (settingsQuery.data && sourceOptions.length && subSourceOptions.length) {
			const fields = settingsQuery.data.static_fields

			initial.current = adaptToState(fields, sourceOptions, subSourceOptions)
			setLead(initial.current.lead)
			setSub(initial.current.sub)
		}
	}, [sourceOptions, subSourceOptions, settingsQuery.data])

	const backLink = `/campaigns/${id}`

	const sendForm: FormEventHandler<HTMLFormElement> = event => {
		event.preventDefault()

		update({
			id,
			static_fields: adaptToServer({ lead, sub })
		})
			.unwrap()
			.catch(console.error)
			.then(() => push(backLink))
	}

	const isLoading =
		settingsQuery.isLoading ||
		optionsQuery.isLoading ||
		subOptionsQuery.isLoading

	return isLoading ? (
		<FullLoader size={40} />
	) : (
		<form className={css.root} onSubmit={sendForm}>
			<div className={clsx('h-2', css.title)}>{campaignName}</div>
			<div className={css.wrapper}>
				<div className={css.dropdown}>
					<label className={clsx('h-5', css.label)} htmlFor={sourceId}>
						Source:
					</label>
					<Select
						id={sourceId}
						isMulti
						onChange={
							setLead as (value: MultiValue<ApiHubSpot.Options>) => void
						}
						options={sourceOptions}
						placeholder='Select...'
						value={lead}
					/>
				</div>

				<div className={css.dropdown}>
					<label className={clsx('h-5', css.label)} htmlFor={subSourceId}>
						Sub Source:
					</label>
					<Select
						id={subSourceId}
						isClearable
						onChange={
							setSub as (value: SingleValue<ApiHubSpot.Options>) => void
						}
						options={subSourceOptions}
						placeholder='Select...'
						value={sub}
					/>
				</div>

				<div className={css.buttons}>
					<Link
						className={clsx(
							css.alt,
							css.button,
							'btn semi-bold text-button-small alt'
						)}
						to={backLink}
					>
						Cancel
					</Link>

					<ResponsiveButton
						className={clsx(css.button, 'btn semi-bold text-button-small')}
						disabled={isInitialValues}
						isLoading={updateOptions.isLoading}
						type='submit'
					>
						Save
					</ResponsiveButton>
				</div>
			</div>
		</form>
	)
}
